import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CustomTypography from "../../globalStyles/fonts";
import AutocompleteWithPagination from "../autocomplete-with-pagination";
import { wmsService } from "../../utils/services/wmsProvider";

const RATE_FIELDS = [
  { key: "inbound_fixed", label: "Inbound / order" },
  { key: "inbound_per_unit", label: "Inbound / unit" },
  { key: "storage_fixed", label: "Storage / order" },
  { key: "storage_per_unit", label: "Storage / unit" },
  { key: "outbound_fixed", label: "Outbound / order" },
  { key: "outbound_per_unit", label: "Outbound / unit" },
];

const FieldLabel = ({ children, required }) => (
  <CustomTypography fontSize={13} style={{ marginBottom: 4 }}>
    {children}
    {required ? <span style={{ color: "red" }}> *</span> : null}
  </CustomTypography>
);

const warehouseOptionLabel = (warehouse) => {
  if (!warehouse) return "";
  if (warehouse.name && warehouse.warehouse_code) {
    return `${warehouse.name} (${warehouse.warehouse_code})`;
  }
  return warehouse.name || warehouse.warehouse_code || "";
};

const CostRateModal = ({
  open,
  clientId,
  form,
  saving,
  viewOnly,
  isEdit,
  onChange,
  onWarehouseChange,
  onClose,
  onSave,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      onClick={(e) => e.stopPropagation()}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography fontFamily="Poppins" fontWeight={600} fontSize={16}>
          {viewOnly
            ? "Warehouse cost rate"
            : isEdit
              ? "Edit warehouse cost rate"
              : "Add warehouse cost rate"}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography
          fontFamily="Poppins"
          fontSize={12}
          color="var(--text-secondary)"
          sx={{ mb: 2 }}
        >
          These rates are used by Supply Chain Cost Analysis. Leave Effective to
          empty for an open-ended card. Saving a new open card closes the
          previous one the day before.
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FieldLabel required>Warehouse</FieldLabel>
            <AutocompleteWithPagination
              hideLabel
              width="100%"
              containerSx={{ width: "100%" }}
              selectedClient={{ _id: clientId }}
              selectedItem={form.warehouse}
              onDataChange={(warehouse) => onWarehouseChange(warehouse || null)}
              optionKey={warehouseOptionLabel}
              placeholder="Search warehouse name or code"
              apiEndPoint={(limit, skip, search, client) =>
                wmsService.fetch(client, limit, skip, null, search)
              }
              disabled={viewOnly || isEdit || !clientId}
              freeSolo={false}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FieldLabel required>Currency</FieldLabel>
            <TextField
              disabled={viewOnly}
              size="small"
              fullWidth
              value={form.currency}
              onChange={(e) => onChange("currency", e.target.value)}
            />
          </Grid>
          {RATE_FIELDS.map((field) => (
            <Grid item xs={12} sm={6} md={4} key={field.key}>
              <FieldLabel required>{field.label}</FieldLabel>
              <TextField
                disabled={viewOnly}
                size="small"
                fullWidth
                type="number"
                inputProps={{ min: 0, step: "0.01" }}
                value={form[field.key]}
                onChange={(e) => onChange(field.key, e.target.value)}
              />
            </Grid>
          ))}
          <Grid item xs={12} sm={6}>
            <FieldLabel required>Effective from</FieldLabel>
            <TextField
              disabled={viewOnly}
              size="small"
              fullWidth
              type="date"
              value={form.effective_from}
              onChange={(e) => onChange("effective_from", e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FieldLabel>Effective to</FieldLabel>
            <TextField
              disabled={viewOnly}
              size="small"
              fullWidth
              type="date"
              value={form.effective_to}
              onChange={(e) => onChange("effective_to", e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="outlined">
          {viewOnly ? "Close" : "Cancel"}
        </Button>
        {viewOnly ? null : (
          <Button
            onClick={onSave}
            variant="contained"
            disabled={saving}
            sx={{ textTransform: "none" }}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CostRateModal;
